import type { CostCenter } from "@/context/FinanceContext";
import { PersistedState } from "./useFinanceState";
import { toast } from "@/hooks/use-toast";

export function useCostCentersOperations(
  state: PersistedState,
  setState: React.Dispatch<React.SetStateAction<PersistedState>>,
  user: any,
  createCostCenter: (data: any) => Promise<string>,
  updateSupabaseCostCenter: (id: string, data: any) => Promise<boolean>,
  deleteCostCenter: (id: string) => Promise<boolean>
) {
  const addCostCenter = async (name: string): Promise<string | null> => {
    // Check for duplicate names
    const existingCostCenter = state.costCenters.find(c => c.name.toLowerCase() === name.toLowerCase());
    if (existingCostCenter) {
      toast({
        variant: "destructive",
        title: "Nome duplicado",
        description: "Já existe um centro de custos com este nome.",
      });
      return null;
    }

    const tempId = `cc-${crypto.randomUUID()}`;
    
    // Sync to Supabase if user is logged in
    if (user) {
      try {
        const supabaseId = await createCostCenter({ name });
        if (supabaseId) {
          // Update local state with the real ID from Supabase
          setState((s) => ({
            ...s,
            costCenters: [...s.costCenters, { id: supabaseId, name }],
          }));
          return supabaseId;
        }
      } catch (error) {
        console.error('Error syncing cost center to Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível salvar o centro de custos na nuvem.",
        });
        return null;
      }
    } else {
      // Update local state with temp ID if not logged in
      setState((s) => ({
        ...s,
        costCenters: [...s.costCenters, { id: tempId, name }],
      }));
      return tempId;
    }
    return null;
  };

  const updateCostCenter = async (id: string, name: string) => {
    // Update local state first
    setState((s) => ({
      ...s,
      costCenters: s.costCenters.map((c) => (c.id === id ? { ...c, name } : c)),
    }));

    // Sync to Supabase if user is logged in
    if (user) {
      try {
        await updateSupabaseCostCenter(id, { name });
      } catch (error) {
        console.error('Error syncing cost center update to Supabase:', error);
      }
    }
  };

  const removeCostCenter = async (id: string) => {
    // Sync to Supabase if user is logged in first
    if (user) {
      try {
        const success = await deleteCostCenter(id);
        if (!success) {
          // Se a exclusão no Supabase falhou, não remove do estado local
          return;
        }
        
        toast({
          title: "Centro de custos excluído",
          description: "O centro de custos foi removido com sucesso.",
        });
      } catch (error) {
        console.error('Error deleting cost center from Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível excluir o centro de custos. Tente novamente.",
        });
        return;
      }
    }

    // Update local state only after successful Supabase deletion (or if offline)
    setState((s) => ({
      ...s,
      costCenters: s.costCenters.filter((c) => c.id !== id),
    }));

    if (!user) {
      toast({
        title: "Centro de custos excluído localmente",
        description: "O centro de custos foi removido localmente. Faça login para sincronizar.",
      });
    }
  };

  return {
    addCostCenter,
    updateCostCenter,
    removeCostCenter,
  };
}
